import { Permissions } from './../../../../core/constants/permissions';
import { AfterViewInit, Component, Input, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { TrialPolicy } from '../trial-policy.model';
import { Router } from '@angular/router';
import { ObjectHelper } from "../../../../core/helpers/object.helper";
import { TrialDetail } from "../../../../core/models/trial-detail.model";
import { TrialService } from "../../../../core/services/trial.service";
import { AuthService } from "../../../../core/services/auth.service";
import { AlertService } from "../../../../shared/alert/alert.service";
import { ModalComponent } from "../../../../shared/modal/modal.component";
import { TrialPolicyCloneComponent } from "./trial-policy-clone/trial-policy-clone.component";
import { Countries } from "../../../../core/constants/countries";

@Component({
  selector: 'app-trial-policies',
  templateUrl: './trial-policies.component.html',
  styleUrls: ['./trial-policies.component.scss']
})

export class TrialPoliciesComponent implements AfterViewInit {
  @ViewChild('removePolicyModal') removePolicyModal: ModalComponent;
  @ViewChild('clonePolicyModal') clonePolicyModal: TrialPolicyCloneComponent;

  @Input() trial = new TrialDetail();

  policies: TrialPolicy[] = [];
  filteredPolicies: TrialPolicy[] = [];
  policyIdToRemove: string = null;
  Permissions = Permissions;

  dataLoaded = false;
  loaderArray = Array.from({ length: 5 });

  filterForm: UntypedFormGroup;
  countryOptions: { value: string, text: string }[] = [];
  selectedCountry: string = '';
  selectedSiteId: string = '';
  policySiteIds: string[] | null = null;
  private allCountryOptions: { value: string, text: string }[] = [];

  constructor(public authService: AuthService, public trialService: TrialService, public alertService: AlertService, public router: Router) {
    for (const country of Countries.all()) {
      this.allCountryOptions.push({ value: country.code, text: country.name });
    }

    this.filterForm = new UntypedFormGroup({
      country: new UntypedFormControl({ value: '', disabled: true }),
      siteId: new UntypedFormControl('')
    });
  }

  ngAfterViewInit() {
    this.loadPolicies();
  }

  loadPolicies() {
    if (!ObjectHelper.isUndefinedOrNull(this.trial)) {
      this.dataLoaded = false;
      this.trialService.policies(this.trial.id).subscribe({
        next: (policies: TrialPolicy[]) => {
          this.policies = policies;
          this.updateCountryOptions();
          this.updatePolicySiteIds();
          this.applyFilter();
          this.dataLoaded = true;
        },
        error: (error) => {
          this.alertService.showErrorAlert(error);
          this.dataLoaded = true;
        }
      });
    }
  }

  onCountryChanged(value: string): void {
    this.selectedCountry = value || '';
    if (this.selectedSiteId) {
      this.selectedSiteId = '';
      this.filterForm.get('siteId').setValue('');
    }
    this.updatePolicySiteIds();
    this.applyFilter();
  }

  onCountryCleared(): void {
    this.onCountryChanged('');
  }

  onSiteSelected(event: { id: string }): void {
    this.selectedSiteId = event?.id || '';
    this.applyFilter();
  }

  onSiteCleared(): void {
    this.selectedSiteId = '';
    this.applyFilter();
  }

  private applyFilter(): void {
    this.filteredPolicies = this.policies.filter(policy => {
      if (this.selectedCountry && policy.countryCode !== this.selectedCountry) {
        return false;
      }
      if (this.selectedSiteId) {
        const matches = policy.allSites
          || (policy.sites && policy.sites.some(s => s.id === this.selectedSiteId));
        if (!matches) {
          return false;
        }
      }
      return true;
    });
  }

  private updateCountryOptions(): void {
    const codes = new Set(this.policies.map(p => p.countryCode).filter(c => !!c));
    this.countryOptions = this.allCountryOptions.filter(o => codes.has(o.value));

    const countryControl = this.filterForm.get('country');
    if (this.countryOptions.length === 0) {
      countryControl.disable();
    } else {
      countryControl.enable();
    }
  }

  private updatePolicySiteIds(): void {
    const policiesForCountry = this.selectedCountry
      ? this.policies.filter(p => p.countryCode === this.selectedCountry)
      : this.policies;

    if (policiesForCountry.some(p => p.allSites)) {
      this.policySiteIds = null;
      return;
    }

    const ids = new Set<string>();
    for (const policy of policiesForCountry) {
      if (policy.sites) {
        for (const site of policy.sites) {
          ids.add(site.id);
        }
      }
    }
    this.policySiteIds = Array.from(ids);
  }

  onClonePolicy(policyId: string) {
    this.clonePolicyModal.show(this.trial.id, policyId);
  }

  onEdit(policyId: string): void {
    this.router.navigate(['/trial/'+this.trial.id+'/policy/'+policyId+'/edit/']);
  }

  /**
   * Called when a user clicks to remove a policy
   * @param policyId
   */
  onRemovePolicy(policyId: string): void {
    this.policyIdToRemove = policyId;
    this.removePolicyModal.show();
  }

  /**
   * Called when a user has confirmed they wish to delete the selected policy
   */
  onConfirmRemovePolicy(): void {
    this.trialService.removePolicy(this.trial.id, this.policyIdToRemove).subscribe({
      next: () => {
        this.loadPolicies();
        this.removePolicyModal.hide();
        this.policyIdToRemove = null;
        this.alertService.showSuccessAlert('Policy successfull removed');
      },
      error: (error) => {
        this.alertService.showErrorAlert(error);
      }
    });
  }
}
